import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { loadUsersForTable, userTableQueryKey, type UserTableRow } from '@/api/admin/user';
import { type ApiResponse } from '@/api/tenantClient';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import { uiStyles } from '@/lib/ui';
import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { useEffect, useState, useCallback } from 'react';
import UserDataTable from './UserDataTable';
import BaseLoadingSpinner from '@/base/Loading/Spinner';
import { type MRT_ColumnFiltersState, type MRT_PaginationState, type MRT_SortingState, type MRT_VisibilityState } from 'mantine-react-table';
import { useLocalStorage } from '@uidotdev/usehooks';

interface Params {
  filters: any
  sorting: any
  pagination: any
  search: string
}

const initialVisibilityState: MRT_VisibilityState = {
  phone: false,
  company: false,
  location: false,
  clientId: false,
  lastSignInAt: false,
  createdAt: false,
  updatedAt: false
};

const useGetUsers = ({ filters, sorting, pagination, search }: Params & { setPagination: React.Dispatch<React.SetStateAction<any>> }) => {
  const { createTenantAdminApiRequest, isLoading: isTenantLoading } = useTenantApi();
  return useQuery<ApiResponse<UserTableRow>, Error>({
    queryKey: [userTableQueryKey, pagination, filters, sorting, search],
    queryFn: async (): Promise<ApiResponse<UserTableRow>> => {
      return await loadUsersForTable(createTenantAdminApiRequest)({
        page: pagination.pageIndex,
        size: pagination.pageSize,
        filters,
        sorting,
        search
      });
    },
    keepPreviousData: true,
    staleTime: 30_000,
    enabled: !isTenantLoading,
  });
};

const LOCAL_TABLE_STORAGE_KEY = 'user_table';

const UserTable = () => {
  const [filters, setFilters] = useLocalStorage<MRT_ColumnFiltersState>(`${LOCAL_TABLE_STORAGE_KEY}_filters`, []);
  const [sorting, setSorting] = useLocalStorage<MRT_SortingState>(`${LOCAL_TABLE_STORAGE_KEY}_sorting`, []);
  const [search, setSearch] = useLocalStorage<string>(`${LOCAL_TABLE_STORAGE_KEY}_search`, '');
  const [pagination, setPagination] = useLocalStorage<MRT_PaginationState>(`${LOCAL_TABLE_STORAGE_KEY}_pagination`, { pageIndex: 0, pageSize: 10 });
  const [columnVisibility, setColumnVisibility] = useLocalStorage<MRT_VisibilityState>(`${LOCAL_TABLE_STORAGE_KEY}_columns`, initialVisibilityState);
  const { reportToGlobal } = useApiErrorHandler();
  const { data, isLoading, isError, error, isFetching, refetch } = useGetUsers({ filters, sorting, pagination, search, setPagination });
  
  useEffect(
    () => {
      if (error) {
        reportToGlobal(error);
      }
    },
    [error]
  );
  
  if (!data || isLoading) {
    return (
      <BaseLoadingSpinner />
    );
  }

  if (data.items && Array.isArray(data.items)) {
    return (
      <UserDataTable
        data={data.items}
        isLoading={isLoading}
        isError={isError}
        isFetching={isFetching}
        refetch={refetch}
        filters={filters}
        setFilters={setFilters}
        sorting={sorting}
        setSorting={setSorting}
        search={search}
        setSearch={setSearch}
        pagination={pagination}
        setPagination={setPagination}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        rowCount={data.pagination!.total}
      />
    );
  } else {
    return (
      <BaseMessageBarInfo borderRadius={uiStyles.borderRadius}>
        No users found
      </BaseMessageBarInfo>
    );
  }
};

export default UserTable;